import { useEffect, useState } from 'react';
import { Alert, Button, Input, InputNumber, Segmented, Select, Skeleton, Space, Typography, message } from 'antd';
import dayjs from 'dayjs';
import {
  addFindingReview,
  getFindingReview,
  type FindingAction,
  type FindingCause,
  type FindingDisposition,
  type FindingReview,
} from '@/api/validationRuns';
import { StatusPill, type PillTone } from '@/components/StatusPill';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// FindingReviewPanel — EVAL-06 (workspace plan §10.4). What people did about one failed or inconclusive result:
// assign, note, classify the cause, resolve (with the correction and a passing retest), reopen, dispute. Every
// action appends a record; the machine result and the history never change. The server decides what is allowed and
// says why when it is not.
// ─────────────────────────────────────────────────────────────────────────────

const DISPOSITION: Record<FindingDisposition, { label: string; tone: PillTone }> = {
  Open: { label: 'Open', tone: 'red' },
  Acknowledged: { label: 'Being looked at', tone: 'amber' },
  Resolved: { label: 'Resolved', tone: 'green' },
  Disputed: { label: 'Disputed', tone: 'violet' },
};

const ACTION_WORDS: Record<FindingAction, string> = {
  Assign: 'Assigned',
  Note: 'Note',
  Classify: 'Classified',
  Resolve: 'Resolved',
  Reopen: 'Reopened',
  Dispute: 'Disputed',
};

export const CAUSE_WORDS: Record<FindingCause, string> = {
  Agent: 'The agent (instructions, skills, tools)',
  Scenario: 'The scenario (its expectation is wrong)',
  Evaluator: 'The checker (it judged wrongly)',
  Infrastructure: 'Infrastructure (timeout, outage)',
};

type Mode = 'Note' | 'Assign' | 'Classify' | 'Resolve' | 'Dispute' | 'Reopen';

const MODE_LABEL: Record<Mode, string> = {
  Note: 'Add note',
  Assign: 'Assign to me',
  Classify: 'Classify',
  Resolve: 'Resolve',
  Dispute: 'Dispute',
  Reopen: 'Reopen',
};

export function FindingReviewPanel({ resultId }: { resultId: number }) {
  const [review, setReview] = useState<FindingReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('Note');
  const [note, setNote] = useState('');
  const [cause, setCause] = useState<FindingCause | undefined>(undefined);
  const [correction, setCorrection] = useState('');
  const [retestId, setRetestId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setReview(null);
    getFindingReview(resultId, controller.signal)
      .then((r) => !controller.signal.aborted && setReview(r))
      .catch((err: Error) => !controller.signal.aborted && setError(err.message));
    return () => controller.abort();
  }, [resultId]);

  const submit = async () => {
    setSaving(true);
    try {
      const updated = await addFindingReview(resultId, {
        action: mode,
        note: note.trim() || undefined,
        classification: mode === 'Classify' ? cause : undefined,
        correctionRef: mode === 'Resolve' ? correction.trim() : undefined,
        retestResultId: mode === 'Resolve' && retestId != null ? retestId : undefined,
      });
      setReview(updated);
      setNote('');
      setCorrection('');
      setRetestId(null);
      void message.success('Recorded.');
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Alert type="error" showIcon title="Could not load the review" description={error} />;
  if (!review) return <Skeleton active paragraph={{ rows: 3 }} />;

  const d = DISPOSITION[review.disposition];
  const canReopen = review.disposition === 'Resolved' || review.disposition === 'Disputed';
  const modes: Mode[] = ['Note', 'Assign', 'Classify', 'Resolve', 'Dispute', ...(canReopen ? (['Reopen'] as Mode[]) : [])];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space size={[8, 8]} wrap>
        <StatusPill tone={d.tone}>{d.label}</StatusPill>
        <Text type="secondary">{review.assigneeName ? `Owner: ${review.assigneeName}` : review.assigneeUserId ? `Owner: user #${review.assigneeUserId}` : 'No owner yet'}</Text>
        {review.cause && <Text type="secondary">{`· Cause: ${CAUSE_WORDS[review.cause]}`}</Text>}
      </Space>
      <Text type="secondary" style={{ fontSize: 12 }}>
        The failure itself is never changed. Resolved needs the change that fixed it and a passing retest from a later run.
        A dispute is recorded beside the result; it does not turn it into a pass.
      </Text>

      <div>
        <Segmented value={mode} onChange={(v) => setMode(v as Mode)} options={modes.map((m) => ({ value: m, label: MODE_LABEL[m] }))} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {mode === 'Classify' && (
            <Select
              placeholder="Where is the problem?"
              value={cause}
              onChange={setCause}
              options={(Object.keys(CAUSE_WORDS) as FindingCause[]).map((c) => ({ value: c, label: CAUSE_WORDS[c] }))}
            />
          )}
          {mode === 'Resolve' && (
            <>
              <Input placeholder="What was changed (e.g. instructions version, skill name, commit)" value={correction} onChange={(e) => setCorrection(e.target.value)} maxLength={400} />
              <InputNumber style={{ width: '100%' }} placeholder="Result id of the passing retest" value={retestId} onChange={(v) => setRetestId(v == null ? null : Number(v))} min={1} />
            </>
          )}
          {mode !== 'Assign' && (
            <Input.TextArea
              rows={2}
              maxLength={4000}
              placeholder={mode === 'Dispute' ? 'Why the result is wrong' : mode === 'Reopen' ? 'Why it is open again (optional)' : 'Note (optional)'}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
          <div>
            <Button type="primary" loading={saving} onClick={() => void submit()}>
              {mode === 'Assign' ? 'Assign to me' : 'Record'}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Text strong>History</Text>
        {review.events.length === 0 ? (
          <Text type="secondary" style={{ display: 'block' }}>Nothing recorded yet.</Text>
        ) : (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {[...review.events].reverse().map((e) => (
              <li key={e.id} style={{ marginBottom: 6 }}>
                <Text>{ACTION_WORDS[e.action]}</Text>
                {e.action === 'Assign' && <Text>{` to ${e.assigneeName ?? `user #${e.assigneeUserId}`}`}</Text>}
                {e.classification && <Text>{`: ${CAUSE_WORDS[e.classification]}`}</Text>}
                {e.correctionRef && <Text>{` — ${e.correctionRef}`}</Text>}
                {e.retestResultId && <Text type="secondary">{` (retest result #${e.retestResultId})`}</Text>}
                {e.note && <Text type="secondary" style={{ display: 'block' }}>{e.note}</Text>}
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {`${e.byName ?? `user #${e.byUserId}`} · ${dayjs(e.atUtc).format('D MMM YYYY HH:mm')}`}
                </Text>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
